import { axiosToken, metricsAxios } from 'api/apiBase/axios';
import { IBaseResponse } from 'api/apiBase/IBaseResponse';
import { mapError } from 'api/apiBase/mapError';
import { mapResponse } from 'api/apiBase/mapResponse';

import {
  GetAccountBalancesRequest,
  GetAccountBalancesResponse,
  GetAccountsRequest,
  GetAccountsResponse,
  SyncAccountBalanceRequest,
  SyncAccountBalanceResponse,
  SyncAccountFeeRequest,
  SyncAccountFeeResponse,
} from './api.types';

export async function getAccountBalances({ account_ids }: GetAccountBalancesRequest) {
  try {
    const urlParams = new URLSearchParams();

    for (const account_id of account_ids) {
      urlParams.append('account_id', account_id.toString());
    }

    const response = await metricsAxios.get<IBaseResponse<GetAccountBalancesResponse>>(
      '/api/v1/accounts/fee?' + urlParams.toString(),
    );
    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<GetAccountBalancesResponse>({ error });
  }
}

/**
 * Retrieves CEX accounts for a specific trading pair with pagination.
 *
 * @param req - Pair ID, limit, and optional lastSeenId for pagination.
 * @param signal - Optional abort controller signal for request cancellation.
 * @returns Promise with accounts list and pagination flag.
 * @see https://api.dev.111pg.info/swagger/#/cex-pairs/CexPairsAccountsListRequest
 */
export async function getAccounts(
  { pairId, limit, lastSeenId }: GetAccountsRequest,
  signal?: AbortSignal,
) {
  try {
    const urlParams = new URLSearchParams();
    urlParams.append('limit', limit.toString());

    if (lastSeenId !== undefined) {
      urlParams.append('last_seen_id', lastSeenId.toString());
    }

    const response = await axiosToken.get<IBaseResponse<GetAccountsResponse>>(
      `/api/cex-pairs/${pairId}/accounts?${urlParams.toString()}`,
      { signal },
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<GetAccountsResponse>({ error });
  }
}

export async function syncAccountBalance(
  { cex, symbol, account_id }: SyncAccountBalanceRequest,
  signal?: AbortSignal,
) {
  try {
    const urlParams = new URLSearchParams();
    urlParams.append('cex', cex);
    urlParams.append('symbol', symbol);
    urlParams.append('account_id', account_id.toString());

    const response = await metricsAxios.get<IBaseResponse<SyncAccountBalanceResponse>>(
      `/api/v1/balance/actual/sync?${urlParams.toString()}`,
      { signal },
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<SyncAccountBalanceResponse>({ error });
  }
}

export async function syncAccountFee({ account_id }: SyncAccountFeeRequest, signal?: AbortSignal) {
  try {
    const urlParams = new URLSearchParams();
    urlParams.append('account_id', account_id.toString());

    const response = await metricsAxios.get<IBaseResponse<SyncAccountFeeResponse>>(
      `/api/v1/accounts/fee/sync?${urlParams.toString()}`,
      { signal },
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<SyncAccountFeeResponse>({ error });
  }
}
